import {
  AnnouncementAlertStyle,
  CreditPlanFailureReason,
  CreditPlanStatus,
  ThumbValue
} from '@/@types/client-api';
import type { ThumbFeedbackValue } from '@/components/ui/Feedback/ThumbFeedbackButtons';
import LoadingDots from '../../Buttons/LoadingDots';
import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react';

export const mapThumbFeedbackUIValueToThumbValue = (thumbValue: ThumbFeedbackValue): ThumbValue => {
  return thumbValue === 'thumbsUp' ? ThumbValue._1 : ThumbValue._0;
};

/**
 * API returns status as number; map to string enum for UI consumption
 * @param n number status from API
 * @returns CreditPlanStatus enum value or undefined
 */
export const mapCreditPlanStatus = (n: number | undefined): CreditPlanStatus | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, CreditPlanStatus> = {
    0: CreditPlanStatus.STARTED,
    1: CreditPlanStatus.EXECUTING,
    2: CreditPlanStatus.COMPLETED,
    3: CreditPlanStatus.FAILED
  };
  return map[n];
};

export const mapCreditPlanFailureReason = (
  n: number | undefined
): CreditPlanFailureReason | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, CreditPlanFailureReason> = {
    0: CreditPlanFailureReason.GENERAL,
    1: CreditPlanFailureReason.NO_CREDIT_REQUIREMENTS,
    2: CreditPlanFailureReason.NO_PROGRAMS_FOUND,
    3: CreditPlanFailureReason.NO_PROGRAMS_PICKED_FOR_PLAN
    // 4: CreditPlanFailureReason.UNABLE_TO_RETRIEVE_PLAN,
    // 5: CreditPlanFailureReason.UNABLE_TO_START_PLAN_GENERATION
  };
  return map[n];
};

export const mapCreditPlanAlertMessage = (
  n: number | undefined
): AnnouncementAlertStyle | undefined => {
  if (n === undefined) return undefined;
  const map: Record<number, AnnouncementAlertStyle> = {
    0: AnnouncementAlertStyle.ANNOUNCEMENT,
    1: AnnouncementAlertStyle.ALERT
  };
  return map[n];
};

/**
 * Get the background image for the region card on the credit plan block
 * @param regionCode (ex: CLE-NY, CLE-CA, etc..)
 * @param isTransitional used to distinguish between transitional/newly admitted vs experienced attorneys
 * @param orientation image orientation, either landscape or portrait
 * @returns string URL of the image
 */
export const getCreditPlanRegionImage = (
  regionCode: string,
  isTransitional: boolean,
  orientation: 'landscape' | 'portrait'
) => {
  const transitionalParam = `${isTransitional ? '?isTransitionalOrNewlyAdmitted=true' : '?isTransitionalOrNewlyAdmitted=false'}`;
  // landscape: max size: width 1280 height 417, from figma
  // portrait: max size: width 410 height 615, from figma
  // TODO: Yes, these could be more responsive to provide better srcset for breakpoints, not currently worth the effort
  const sizeParams =
    orientation === 'landscape' ? '&width=1280&height=417' : '&width=410&height=615';
  return `${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/credit-plans/image/${regionCode}/${orientation}${transitionalParam}${sizeParams}`;
};

/**
 * Get the credit plan card button label.
 * @param status
 * @returns
 */
export const getCreditPlanCardButtonLabel = (status: CreditPlanStatus) => {
  switch (status) {
    case CreditPlanStatus.STARTED:
    case CreditPlanStatus.EXECUTING:
      return (
        <span className="flex items-center gap-2">
          <LoadingDots dotSize="small" />
          Generating plan
        </span>
      );
    case CreditPlanStatus.FAILED:
    case CreditPlanStatus.COMPLETED:
      return (
        <span className="flex items-center gap-2 whitespace-nowrap">
          Browse plan <ChevronRightIcon className="-mr-2 size-4" />
        </span>
      );
  }
};

export const buildCreditPlanLicenseIdentifier = (
  indivSk: string | number,
  jurisdiction: string,
  flag?: string
) => {
  return `${indivSk}:${jurisdiction}:${flag ?? ''}`;
};

export const deconstructCreditPlanLicenseIdentifier = (licenseIdentifier: string) => {
  const parts = licenseIdentifier?.split(':') ?? [];
  const [indivSk = '', jurisdiction = '', flag = ''] = parts;
  return { indivSk, jurisdiction, flag };
};
